import type { ICampaignTemplate } from '~/modules/campaign-template/types';

import { ESetupGuideCampaignTemplate } from '~/modules/campaign-template/types';
import { EmptyVariantSetupCard } from '~/modules/campaign/modules/variant';
import { IAssetTemplateModalType } from '~/modules/asset-template/types';
import { findVariantByType, ICampaignType } from '~/modules/campaign';
import { useAssetTemplateContext } from '~/modules/asset-template';
import { GSetupGuideItemWrapper } from '~/presentation/common';
import { IVariantType } from '~/modules/campaign/types';
import { useTranslation } from 'react-i18next';
import { Button } from '@shopify/polaris';

export interface EmptyVariantTemplateSuffixProps {
  children?: React.ReactNode;
  campaign: ICampaignTemplate;
  allowTestingBetweenOtherPageType?: boolean;
}

const VARIANT_TO_MODAL_TYPE: Record<
  IVariantType,
  IAssetTemplateModalType.VARIANT_A | IAssetTemplateModalType.VARIANT_B
> = {
  [IVariantType.VARIANT_A]: IAssetTemplateModalType.VARIANT_A,
  [IVariantType.VARIANT_B]: IAssetTemplateModalType.VARIANT_B,
};

const SETUP_GUIDE_ID_BY_MODAL_TYPE: Record<
  IAssetTemplateModalType.VARIANT_A | IAssetTemplateModalType.VARIANT_B,
  ESetupGuideCampaignTemplate
> = {
  [IAssetTemplateModalType.VARIANT_A]: ESetupGuideCampaignTemplate.CHOOSE_TEMPLATE_TO_TEST,
  [IAssetTemplateModalType.VARIANT_B]: ESetupGuideCampaignTemplate.SETUP_TEST_VERSION,
};

export const EmptyVariantTemplateSuffix: React.FC<EmptyVariantTemplateSuffixProps> = ({
  campaign,
  allowTestingBetweenOtherPageType,
}) => {
  const { t } = useTranslation();
  const { modalAssetTemplate } = useAssetTemplateContext();
  const { handleSetModalType, handleSetModalData } = modalAssetTemplate;

  const handleSelectTemplate = (modalType: IAssetTemplateModalType) => {
    if (!campaign) return;
    const variantType =
      modalType === IAssetTemplateModalType.VARIANT_A
        ? IVariantType.VARIANT_A
        : IVariantType.VARIANT_B;
    const variant = findVariantByType(campaign, variantType);
    if (!variant) return;

    handleSetModalType(modalType);
    handleSetModalData({ variant, campaign, allowTestingBetweenOtherPageType });
  };

  // Variant button stays disabled until Control is picked. In this empty-state
  // component every variant is empty by definition, so Variant is always disabled here.
  const isButtonDisabled = (modalType: IAssetTemplateModalType) =>
    modalType === IAssetTemplateModalType.VARIANT_B || !campaign || !campaign.variants?.length;

  const renderButton = (variantType: IVariantType) => {
    const modalType = VARIANT_TO_MODAL_TYPE[variantType];
    return (
      <GSetupGuideItemWrapper setupId={SETUP_GUIDE_ID_BY_MODAL_TYPE[modalType]} variant="button">
        <Button
          disabled={isButtonDisabled(modalType)}
          variant="primary"
          onClick={() => handleSelectTemplate(modalType)}
        >
          {t('Select a template')}
        </Button>
      </GSetupGuideItemWrapper>
    );
  };

  return (
    <EmptyVariantSetupCard
      campaignType={ICampaignType.TEMPLATE}
      variants={[IVariantType.VARIANT_A, IVariantType.VARIANT_B]}
      renderButton={renderButton}
    />
  );
};
